import { formatarDuracao } from '@projetive-op/domain'
import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowRight, TriangleAlert } from 'lucide-react'

import { EsqueletoCartoes, EsqueletoLinhas } from '@/components/ui/estado'
import { Numero } from '@/components/ui/numero'
import { useRelogio } from '@/hooks/use-relogio'
import { cn } from '@/lib/cn'
import { plural } from '@/lib/formato'
import { primeiroNome } from '@/lib/nomes'
import { decorridoSeg, estourou } from '@/lib/postos'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/')({ component: VisaoGeral })

/**
 * Visão geral da PCP: os números do momento, cada posto agora (com quem está
 * nele — dado individual só aqui, nunca na TV) e, ao lado, a produção do dia
 * e os pedidos atrasados.
 */
function VisaoGeral() {
  const trpc = useTRPC()
  const agora = useQuery({ ...trpc.painel.agora.queryOptions(), refetchInterval: 15_000 })
  const producao = useQuery({
    ...trpc.painel.producaoDoDia.queryOptions(),
    refetchInterval: 60_000,
  })
  const atrasados = useQuery({ ...trpc.pedidos.atrasados.queryOptions(), refetchInterval: 60_000 })
  const relogio = useRelogio()

  const postos = agora.data ?? []
  const ocupados = postos.filter((p) => p.fichaAtual).length
  const logados = postos.filter((p) => p.operador).length
  const pecasHoje = producao.data?.produtos.reduce((n, p) => n + p.pecas, 0) ?? 0
  const atualizadoEm = agora.dataUpdatedAt
    ? new Date(agora.dataUpdatedAt).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  const porLinha = new Map<string, typeof postos>()
  for (const p of postos) {
    const chave = p.linha ?? 'Outros'
    porLinha.set(chave, [...(porLinha.get(chave) ?? []), p])
  }
  // Fila por setor aparece uma vez, mesmo com vários postos no setor.
  const filaPorSetor = new Map(postos.map((p) => [p.setor, p.fichasNaFilaDoSetor]))
  const naFila = [...filaPorSetor.values()].reduce((n, f) => n + f, 0)

  return (
    <>
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-2xl font-bold">Visão geral</h1>
        {atualizadoEm && (
          <p className="text-sm text-fg-subtle tabular">Atualizado às {atualizadoEm}</p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {agora.data ? (
          <>
            <Numero
              tamanho="xl"
              rotuloEmCima
              alinhar="esquerda"
              className="p-5"
              rotulo="Postos produzindo"
              valor={`${ocupados} / ${postos.length}`}
            />
            <Numero
              tamanho="xl"
              rotuloEmCima
              alinhar="esquerda"
              className="p-5"
              rotulo="Operadores logados"
              valor={String(logados)}
            />
            <Numero
              tamanho="xl"
              rotuloEmCima
              alinhar="esquerda"
              className="p-5"
              rotulo="Peças prontas hoje"
              valor={pecasHoje.toLocaleString('pt-BR')}
            />
            <Numero
              tamanho="xl"
              rotuloEmCima
              alinhar="esquerda"
              className="p-5"
              rotulo="Fichas na fila dos setores"
              valor={naFila.toLocaleString('pt-BR')}
            />
          </>
        ) : (
          <EsqueletoCartoes quantidade={4} />
        )}
      </div>

      <div className="mt-6 grid items-start gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <section aria-labelledby="postos-agora" className="flex min-w-0 flex-col gap-4">
          <div className="flex items-baseline justify-between gap-3">
            <h2 id="postos-agora" className="text-lg font-semibold">
              Postos agora
            </h2>
            <Link
              to="/painel"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
            >
              Abrir o painel da TV <ArrowRight className="size-4" aria-hidden />
            </Link>
          </div>
          {agora.isPending && <EsqueletoLinhas linhas={6} />}
          {[...porLinha.entries()].map(([linha, lista]) => (
            <div key={linha} className="overflow-hidden card">
              <h3 className="border-b border-border bg-elevated/40 px-4 py-2 text-xs font-semibold tracking-widest text-fg-muted uppercase">
                {linha}
              </h3>
              <ul className="divide-y divide-border">
                {lista.map((p) => {
                  const f = p.fichaAtual
                  const pausada = f?.estado === 'PAUSADA'
                  const estourada = estourou(f, relogio)
                  return (
                    <li key={p.id} className="flex items-center gap-3 px-4 py-2.5">
                      <span
                        aria-hidden
                        className={cn(
                          'size-2.5 shrink-0 rounded-full',
                          !f
                            ? 'bg-border-strong'
                            : pausada
                              ? 'bg-amarelo'
                              : estourada
                                ? 'bg-vermelho'
                                : 'bg-verde',
                        )}
                      />
                      <span className="w-48 shrink-0 truncate font-medium">{p.nome}</span>
                      <span className="min-w-0 flex-1 truncate text-sm text-fg-muted">
                        {f
                          ? `#${f.ficha.itemPedido.pedido.numero} · ${f.ficha.itemPedido.produto.nome}`
                          : p.operador
                            ? 'Livre'
                            : 'Sem operador'}
                      </span>
                      <span className="hidden w-28 shrink-0 truncate text-sm text-fg-subtle 2xl:block">
                        {p.operador ? primeiroNome(p.operador.nome) : ''}
                      </span>
                      <span
                        className={cn(
                          'w-32 shrink-0 text-right text-sm font-semibold whitespace-nowrap tabular',
                          pausada ? 'text-amarelo' : estourada ? 'text-vermelho' : 'text-fg',
                        )}
                      >
                        {f
                          ? pausada
                            ? 'Pausada'
                            : `${formatarDuracao(decorridoSeg(f, relogio))} / ${formatarDuracao(f.tempoPrevistoSeg)}`
                          : ''}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </section>

        <div className="flex min-w-0 flex-col gap-6">
          <section aria-labelledby="producao-dia" className="card p-5">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <h2 id="producao-dia" className="text-lg font-semibold">
                Produção do dia
              </h2>
              <span className="text-sm font-semibold text-fg-muted tabular">
                {plural(pecasHoje, 'peça', 'peças')}
              </span>
            </div>
            {producao.isPending && <EsqueletoLinhas linhas={4} />}
            <ul className="flex flex-col gap-3">
              {producao.data?.produtos
                .filter((p) => p.pecas > 0)
                .slice(0, 8)
                .map((p) => (
                  <li key={p.produto.id} className="flex items-center gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{p.produto.nome}</span>
                      <span className="block text-xs text-fg-subtle">
                        {plural(p.fichasConcluidas, 'ficha pronta', 'fichas prontas')} ·{' '}
                        {p.fichasEmProducao} em produção
                      </span>
                    </span>
                    <span className="shrink-0 text-xl font-bold tabular">{p.pecas}</span>
                  </li>
                ))}
              {producao.data && !producao.data.produtos.some((p) => p.pecas > 0) && (
                <li className="text-sm text-fg-subtle">Nada produzido ainda hoje.</li>
              )}
            </ul>
          </section>

          <section aria-labelledby="atrasados" className="card p-5">
            <h2 id="atrasados" className="mb-3 flex items-center gap-2 text-lg font-semibold">
              {!!atrasados.data?.length && (
                <TriangleAlert className="size-5 text-vermelho" aria-hidden />
              )}
              Pedidos atrasados
              {!!atrasados.data?.length && (
                <span className="rounded-full bg-vermelho/20 px-2 text-sm text-vermelho tabular">
                  {atrasados.data.length}
                </span>
              )}
            </h2>
            {atrasados.isPending && <EsqueletoLinhas linhas={2} />}
            <ul className="flex flex-col gap-2.5">
              {atrasados.data?.slice(0, 8).map((p) => (
                <li key={p.id}>
                  <Link
                    to="/gestao/pedidos/$pedidoId"
                    params={{ pedidoId: p.id }}
                    className="flex items-center gap-3 rounded-lg hover:text-brand"
                  >
                    <span className="min-w-12 shrink-0 font-semibold tabular">#{p.numero}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-fg-muted">
                      {p.itens.map((i) => i.produto.nome).join(', ')}
                    </span>
                    <span className="shrink-0 text-sm font-bold text-vermelho tabular">
                      {plural(p.diasAtraso, 'dia', 'dias')}
                    </span>
                    <span className="w-10 shrink-0 text-right text-sm text-fg-muted tabular">
                      {p.percentual}%
                    </span>
                  </Link>
                </li>
              ))}
              {atrasados.data?.length === 0 && (
                <li className="text-sm text-fg-subtle">Nenhum pedido atrasado.</li>
              )}
            </ul>
          </section>
        </div>
      </div>
    </>
  )
}
